import React, { useState } from 'react';
import { 
  MapPin, Search, Bell, ChevronDown, Sliders, 
  Trophy, Dumbbell, Timer, QrCode,
  CalendarDays, AlertCircle, BadgeCheck, Building,
  Flag, Globe
} from 'lucide-react';

const MembershipView = () => {
  // Estados
  const [searchTerm, setSearchTerm] = useState('');
  const [showExpired, setShowExpired] = useState(false);
  const [showQRCode, setShowQRCode] = useState(null);
  const [notifications, setNotifications] = useState(1);

  const memberships = [
    {
      id: 1,
      name: "Elite Fitness Club",
      type: "Gym",
      location: "Downtown NY",
      startDate: "2024-01-01",
      expiryDate: "2024-12-31",
      status: "active",
      price: 49.99,
      image: "/api/placeholder/800/400",
      membershipId: "EF-2024-001",
      features: ["24/7 Access", "All Classes", "Personal Trainer"],
      icon: <Dumbbell className="w-6 h-6" />
    },
    {
      id: 2,
      name: "Green Valley Golf Club",
      type: "Golf",
      location: "Long Island",
      startDate: "2024-02-01",
      expiryDate: "2025-01-31",
      status: "active",
      price: 199.99,
      image: "/api/placeholder/800/400",
      membershipId: "GV-2024-123",
      features: ["Course Access", "Club House", "Equipment Storage"],
      icon: <Flag className="w-6 h-6" />
    },
    {
      id: 3,
      name: "City Sports Complex",
      type: "Sports Club",
      location: "Brooklyn",
      startDate: "2023-06-01",
      expiryDate: "2024-01-31",
      status: "expired",
      price: 79.99,
      image: "/api/placeholder/800/400",
      membershipId: "CS-2023-456",
      features: ["Multiple Sports", "Events Access", "Locker Room"],
      icon: <Trophy className="w-6 h-6" />
    }
  ];

  const filterMemberships = () => {
    return memberships.filter(membership => {
      const matchesSearch = membership.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          membership.type.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = showExpired ? true : membership.status === 'active';
      return matchesSearch && matchesStatus;
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysUntilExpiry = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="max-w-md mx-auto bg-gray-50 min-h-screen pb-20">
      {/* Header */}
      <div className="p-4 space-y-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Building className="w-6 h-6 text-orange-500" />
            <div>
              <h2 className="text-sm text-gray-500">My Memberships</h2>
              <div className="flex items-center">
                <span className="font-semibold">Active & Expired</span>
                <ChevronDown className="w-4 h-4 ml-1" />
              </div>
            </div>
          </div>
          <button className="relative">
            <Bell className="w-6 h-6 text-gray-700" />
            {notifications > 0 && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            )}
          </button>
        </div>

        {/* Search Bar */}
        <div className="flex gap-2">
          <div className="flex-1 bg-white rounded-full border border-gray-200 px-4 py-2.5 flex items-center">
            <Search className="w-5 h-5 text-gray-400 mr-2" />
            <input 
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search memberships"
              className="w-full bg-transparent outline-none text-sm"
            />
          </div>
          <button 
            className={`p-3 rounded-xl transition-colors ${showExpired ? 'bg-orange-500' : 'bg-orange-400'}`}
            onClick={() => setShowExpired(!showExpired)}
          >
            <Timer className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="px-4 mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-500">2</div>
              <div className="text-xs text-gray-600">Active</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-700">1</div>
              <div className="text-xs text-gray-600">Expired</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-500">3</div>
              <div className="text-xs text-gray-600">Total</div>
            </div>
          </div>
        </div>
      </div>

      {/* Memberships List */}
      <div className="px-4">
        <div className="space-y-4">
          {filterMemberships().map(membership => (
            <div key={membership.id} className="bg-white rounded-2xl overflow-hidden shadow-sm">
              <div className="relative">
                <img 
                  src={membership.image} 
                  alt={membership.name}
                  className="w-full h-32 object-cover"
                />
                <div className="absolute top-4 right-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    membership.status === 'active' 
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {membership.status === 'active' ? 'Active' : 'Expired'}
                  </span>
                </div>
                <div className="absolute top-4 left-4">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                    {membership.icon}
                  </div>
                </div>
              </div>

              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold text-lg">{membership.name}</h3>
                    <p className="text-sm text-gray-600">{membership.type}</p>
                  </div>
                  <span className="text-lg font-bold text-orange-500">
                    ${membership.price}
                    <span className="text-sm text-gray-500">/mo</span>
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-gray-600">
                    <MapPin className="w-4 h-4 mr-2" />
                    <span className="text-sm">{membership.location}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <CalendarDays className="w-4 h-4 mr-2" />
                    <span className="text-sm">
                      Expires: {formatDate(membership.expiryDate)}
                    </span>
                  </div>
                  {membership.status === 'active' && getDaysUntilExpiry(membership.expiryDate) > 0 && (
                    <div className="flex items-center text-gray-600">
                      <Timer className="w-4 h-4 mr-2" />
                      <span className="text-sm">
                        {getDaysUntilExpiry(membership.expiryDate)} days remaining
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {membership.features.map((feature, index) => (
                    <span 
                      key={index}
                      className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs flex items-center"
                    >
                      <BadgeCheck className="w-3 h-3 mr-1" />
                      {feature}
                    </span>
                  ))}
                </div>

                <div className="flex gap-2">
                  {membership.status === 'active' && (
                    <button 
                      onClick={() => setShowQRCode(membership.id)}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-xl text-sm font-medium flex items-center justify-center gap-2"
                    >
                      <QrCode className="w-4 h-4" />
                      Ver Pase Digital
                    </button>
                  )}
                  <button className="flex-1 border border-gray-200 hover:bg-gray-50 px-4 py-2 rounded-xl text-sm font-medium">
                    Detalles
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filterMemberships().length === 0 && (
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">No memberships found.</p>
          </div>
        )}
      </div>

      {/* QR Code Modal */}
      {showQRCode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold mb-4">Digital Pass</h3>
            <div className="bg-gray-100 p-4 rounded-xl mb-4">
              <img 
                src="/api/placeholder/300/300"
                alt="QR Code"
                className="w-full aspect-square object-contain"
              />
            </div>
            <p className="text-sm text-gray-600 mb-4 text-center">
              Membership ID: {memberships.find(m => m.id === showQRCode)?.membershipId}
            </p>
            <button 
              onClick={() => setShowQRCode(null)}
              className="w-full bg-gray-900 text-white py-2 rounded-xl"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipView;